import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type { CacheStore } from '@inter-ikea-kompis/utilities';

import IframeManager from '../../utilities/IframeManager.js';

/**
 * This class is used for setting and getting zip code.
 *
 * When the INGKA Javascript API is available on the same page, we can use it directly.
 * This is the case when the footer fragment from INGKA is included using ESI include, or if INGKA includes the planner directly on the page using ESI include.
 *
 * When a planner is running under /addon-app/, it is not possible to include the footer fragment from INGKA as we are not under a retail unit path (e.g. /gb/en/).
 * Then we can use an iframe with a URL that contains a retail unit path to load the INGKA Javascript API.
 */
export default class OneWebZipCodeCookieInformationService {
	private readonly endpoint: string;
	private readonly cacheStore: CacheStore;
	private iframeManager: IframeManager | null;

	constructor(options: { endpoint: string; cacheStore: CacheStore }) {
		this.endpoint = options.endpoint;
		this.cacheStore = options.cacheStore;
	}

	/**
	 * Returns zip code.
	 *
	 * @returns Zip code.
	 */
	public async getZipCode(): Promise<string | null> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((window as any).ikea?.postalCode?.get) {
			return (await this.getIngkaApi(window).get())?.postalCode || null;
		}

		const iframe = await this.getIframe();
		return (await this.getIngkaApi(<Window>iframe.contentWindow).get())?.postalCode || null;
	}

	/**
	 * Sets zip code.
	 *
	 * @param zipCode Zip code.
	 */
	public async setZipCode(zipCode: string | null): Promise<void> {
		if (!zipCode) {
			return await this.clearZipCode();
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((window as any).ikea?.postalCode?.set) {
			await this.getIngkaApi(window).set({ postalCode: zipCode, source: 'NAV' });
			return;
		}

		const iframe = await this.getIframe();
		await this.getIngkaApi(<Window>iframe.contentWindow).set({
			postalCode: zipCode,
			source: 'NAV'
		});
	}

	/**
	 * Clears zip code.
	 */
	public async clearZipCode(): Promise<void> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((window as any).ikea?.postalCode?.clear) {
			await this.getIngkaApi(window).clear();
			return;
		}

		const iframe = await this.getIframe();
		await this.getIngkaApi(<Window>iframe.contentWindow).clear();
	}

	/**
	 * Returns INGKA store Javascript API.
	 *
	 * @param targetWindow Window.
	 * @returns INGKA API.
	 */
	private getIngkaApi(targetWindow: Window): {
		// TODO: Any more sources than NAV?
		get: () => Promise<{ postalCode: string; source: 'NAV' }>;
		set: (postalInfo: { postalCode: string; source: 'NAV' }) => Promise<void>;
		clear: () => Promise<void>;
	} {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if (!(targetWindow as any).ikea?.postalCode) {
			throw new ServiceException(
				'Failed to retrieve zip code API. "ikea.postalCode" is not defined.'
			);
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return (targetWindow as any).ikea.postalCode;
	}

	/**
	 * Returns iframe element.
	 *
	 * @returns Iframe.
	 */
	private async getIframe(): Promise<HTMLIFrameElement> {
		if (this.iframeManager && this.iframeManager.url !== this.endpoint) {
			this.iframeManager.disconnect();
			this.iframeManager = null;
		}

		if (!this.iframeManager) {
			this.iframeManager = new IframeManager(this.endpoint, this.cacheStore);
		}

		return await this.iframeManager.getIframe();
	}
}
